import {
  ApiError,
  activateLocation,
  deactivateLocation,
  getLocation,
  updateLocation,
} from '@jq4race/api-client';
import { LocalizedError, useTranslation } from '@jq4race/i18n';
import type { LocationResponse, UpdateLocationRequest } from '@jq4race/shared';
import { Badge, buttonVariants, Card, CardContent, Skeleton } from '@jq4race/ui';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';
import { PageHeader } from '../../../app-shell/ui/PageHeader';
import { DeactivateLocationDialog } from '../components/DeactivateLocationDialog';
import { LocationForm } from '../components/LocationForm';
import { STATUS_TONE } from '../constants';

export function EditLocationPage() {
  const { t } = useTranslation('locations');
  const { locationId } = useParams({ strict: false });
  const navigate = useNavigate();

  const [location, setLocation] = useState<LocationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogAction, setDialogAction] = useState<'deactivate' | 'activate' | null>(null);

  const loadLocation = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getLocation(locationId);
      setLocation(result);
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('page.edit.loadError') }));
    } finally {
      setLoading(false);
    }
  }, [locationId, t]);

  useEffect(() => {
    void loadLocation();
  }, [loadLocation]);

  async function handleSubmit(values: UpdateLocationRequest) {
    if (!locationId) return;
    try {
      await updateLocation(locationId, values);
      void navigate({ to: '/dashboard/locations' });
    } catch (e) {
      if (e instanceof ApiError) {
        throw new LocalizedError(translateApiError(e, t, { fallback: t('page.edit.updateError') }));
      }
      throw e;
    }
  }

  async function handleStatusConfirmed(loc: LocationResponse) {
    try {
      if (dialogAction === 'deactivate') {
        const updated = await deactivateLocation(loc.id);
        setLocation(updated);
      } else if (dialogAction === 'activate') {
        const updated = await activateLocation(loc.id);
        setLocation(updated);
      }
    } catch (e) {
      setError(translateApiError(e, t, { fallback: t('feedback.operationFailed') }));
    } finally {
      setDialogAction(null);
    }
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !location) {
    return (
      <div className="rounded-sm border border-jq-danger/40 bg-jq-danger/10 px-4 py-3 text-[13px] text-jq-danger">
        {error ?? t('page.edit.notFound')}
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow={t('page.eyebrow')}
        title={t('page.edit.title', { name: location.name })}
        description={t('page.edit.description')}
        meta={
          <Badge variant={STATUS_TONE[location.status]} className="capitalize">
            {t(`status.${location.status}`)}
          </Badge>
        }
        actions={
          <div className="flex items-center gap-2">
            {location.status === 'active' ? (
              <button
                type="button"
                className={`${buttonVariants({ size: 'sm', variant: 'ghost' })} text-jq-danger hover:text-jq-danger`}
                onClick={() => setDialogAction('deactivate')}
              >
                {t('actions.deactivate')}
              </button>
            ) : (
              <button
                type="button"
                className={buttonVariants({ size: 'sm', variant: 'ghost' })}
                onClick={() => setDialogAction('activate')}
              >
                {t('actions.activate')}
              </button>
            )}
            <Link
              to="/dashboard/locations"
              className={buttonVariants({ size: 'sm', variant: 'ghost' })}
            >
              <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
              {t('page.edit.backToList')}
            </Link>
          </div>
        }
      />

      <Card className="border-jq-sand/70 bg-jq-surface-0 shadow-sm">
        <CardContent className="p-6 sm:p-8">
          <LocationForm
            mode="edit"
            defaultValues={{
              name: location.name,
              address: location.address,
              timezone: location.timezone,
              currency: location.currency,
              niche: location.niche,
              vehicleCapacity: location.vehicleCapacity,
            }}
            onSubmit={handleSubmit}
            submitLabel={t('form.saveChanges')}
            onCancel={() => void navigate({ to: '/dashboard/locations' })}
          />
        </CardContent>
      </Card>

      <DeactivateLocationDialog
        location={dialogAction ? location : null}
        action={dialogAction ?? 'deactivate'}
        onClose={() => setDialogAction(null)}
        onConfirmed={handleStatusConfirmed}
      />
    </div>
  );
}
